import { Chip, Stack, TableCell, TableRow } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { deviceListEntryInterface } from '../../../models/interfaces/device_interfaces';
import { device_controller } from '../../../controllers/device_controller';
import {
  alphanumeric_comparator,
  unix_timestamp_to_date_time,
} from '../../../utils/helper_functions';
import { useTranslation } from '../../../contexts/TranslationContext';
import {
  type DeviceHeader,
  selectedDeviceStateAtom,
} from '../../../models/atoms/device_atoms';
import { MHMTypography } from '../MHM/MHMTypography';
import { MHMLabel } from '../MHM/MHMLabel';
import { ALERT_LABELS } from './DeviceList';

type Props = {
  device: deviceListEntryInterface;
  columns: DeviceHeader[];
};

export function DevicesTableEntry({ device, columns }: Props) {
  const selectedDevice = useRecoilValue(selectedDeviceStateAtom);
  const { t } = useTranslation();
  const { language } = useTranslation();

  const labels = device.labels.slice().sort(alphanumeric_comparator);

  function handleSelectDevice() {
    if (
      device.device_id === selectedDevice?.device_id &&
      device.session_id === selectedDevice?.session_id
    ) {
      return;
    }

    device_controller.select_device(device.device_id, device.session_id);
  }

  function handlePinDevice() {
    device_controller.toggle_device_pin(device.device_id, device.session_id);
  }

  return (
    <TableRow
      data-testid={`device-row-${device.device_id}${device.session_id ? `-${device.session_id}` : ''}`}
      sx={{
        bgcolor:
          selectedDevice?.device_id === device.device_id &&
          selectedDevice?.session_id === device.session_id
            ? 'highlightedBackground.main'
            : 'inherit',
      }}
      onClick={handleSelectDevice}
    >
      {columns.map(col => (
        <TableCell key={col} align="center">
          {(() => {
            switch (col) {
              case 'labels':
                return (
                  <Stack gap={1} alignItems="center">
                    {labels.map(l => (
                      <MHMLabel
                        key={l}
                        label={t(l)}
                        labelProps={{ bold: true }}
                        color={ALERT_LABELS.includes(l) ? 'error' : 'primary'}
                        sx={{ flexBasis: 0, maxWidth: 'fit-content' }}
                      />
                    ))}
                  </Stack>
                );
              case 'pinned':
                return (
                  <Chip
                    label={
                      <MHMTypography
                        bold
                        color={device.pinned ? 'highlight' : 'textPrimary'}
                      >
                        {t(device.pinned ? 'Y' : 'N')}
                      </MHMTypography>
                    }
                    color={device.pinned ? 'primary' : 'default'}
                    sx={{
                      borderRadius: '5px',
                      width: '100%',
                      minWidth: '5rem',
                    }}
                    onClick={handlePinDevice}
                  />
                );
              case 'creation_date':
              case 'end_date': {
                const date = device[col]
                  ? unix_timestamp_to_date_time(
                      device[col] ?? 0,
                      false,
                      true,
                      false,
                      language,
                    ).toLocaleString()
                  : t('Unknown');
                return <MHMTypography bold>{date}</MHMTypography>;
              }
              case 'current_level_percentage':
                return (
                  <MHMTypography bold>
                    {Number(device[col] ?? 0).toFixed(1)}%
                  </MHMTypography>
                );
              default:
                return <MHMTypography bold>{device[col]}</MHMTypography>;
            }
          })()}
        </TableCell>
      ))}
    </TableRow>
  );
}
